import { useState } from "react";
import { Info, ChevronDown, ChevronUp } from "lucide-react";
import { LtvView } from "@/components/LtvView";
import { OverviewCards, PopularSection } from "@/components/StaffView";
import { DepartmentGoals } from "@/components/DepartmentGoals";
import { ScoreboardPodier } from "@/components/ScoreboardPodier";
import {
  KPICirclesRow,
  OmsætningBar,
  MonthHistoryRow,
  BookingBar,
  calcOverallScore,
} from "@/components/ManagerKPIView";

// Lille sektions-overskrift med valgfri "i"-tooltip (title-attribut).
function SectionTitle({ children, info }) {
  return (
    <div className="flex items-center gap-1.5 mb-3">
      <h3 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">
        {children}
      </h3>
      {info && (
        <Info
          className="w-3.5 h-3.5 text-muted-foreground/70 cursor-help"
          title={info}
        />
      )}
    </div>
  );
}

// Opfølgning #45/#47.6: samlet "Performance"-fane. Omsætnings-tal øverst,
// KPI-detaljer samlet i en foldet sektion nederst. By-dropdown + periodevælger
// renderes af Dashboard.js UDENFOR denne komponent.
export function PerformanceView({
  token,
  staffData,
  managerData,
  period,
  setPeriod,
  periodOptions = [],
  adminKpiCity,
  setAdminKpiCity,
  adminCities = [],
  cityLabels = {},
  kanVaelgeBy = false,
  isAdmin,
  lang = "da",
  refreshing,
}) {
  // #47.6: KPI-detaljer foldet sammen som default.
  const [kpiOpen, setKpiOpen] = useState(false);
  const { score: overallScore } = managerData
    ? calcOverallScore(managerData)
    : { score: null };

  return (
    <div className="space-y-8 animate-fade-in-up opacity-0 stagger-1">
      {/* Periode-faner renderes af Dashboard.js i TabsContent (samme placering +
          afstand som Score), så alle sider er ens. */}

      {/* I dag-kasser — "I DAG"-overskrift fjernet (kasserne hedder selv "... i dag"). */}
      {staffData && (
        <section>
          <OverviewCards data={staffData} period={period} lang={lang} />
        </section>
      )}

      {/* OMSÆTNING & MÅL PR. BY */}
      {staffData && (
        <section>
          <SectionTitle info="Dagens/periodens omsætning pr. by sammenholdt med skolens mål og breakeven.">
            Omsætning & mål pr. by
          </SectionTitle>
          <DepartmentGoals
            departmentRanking={staffData.department_ranking}
            dayData={staffData.day_by_dept}
            weekData={staffData.week_by_dept}
            monthData={
              period === "last_month"
                ? staffData.last_month_by_dept
                : period === "prev_month"
                ? staffData.prev_month_by_dept
                : staffData.month_by_dept
            }
            period={period}
            lang={lang}
            streakByDept={staffData.streak_by_dept}
            breakevenStreakByDept={staffData.breakeven_streak_by_dept}
          />
        </section>
      )}

      {/* OMSÆTNING 6 MDR */}
      {managerData && (
        <section>
          <SectionTitle>Omsætning 6 mdr.</SectionTitle>
          <OmsætningBar data={managerData} />
        </section>
      )}

      {/* SCOREBOARDS (#48.5): top 3-byer som podie — indeværende måned. */}
      {staffData?.month_department_ranking?.length > 0 && (
        <section>
          <SectionTitle info="Top 3-byer for indeværende måned: omsætning, gavekort/klippekort og produktsalg.">
            Scoreboards · denne måned
          </SectionTitle>
          <ScoreboardPodier ranking={staffData.month_department_ranking} />
        </section>
      )}

      {/* CUSTOMER LIFETIME VALUE (kun admin) */}
      {isAdmin && (
        <section>
          <SectionTitle info="Gennemsnitlig samlet købsværdi pr. kunde med besøg siden 01.01.2021.">
            Customer Lifetime Value
          </SectionTitle>
          <LtvView token={token} />
        </section>
      )}

      {/* POPULÆRE PRODUKTER / BEHANDLINGER */}
      {staffData && (
        <section>
          <PopularSection data={staffData} lang={lang} />
        </section>
      )}

      {/* KPI-DETALJER — foldet sammen som default. #48.4: tydeligere foldbar
          header (større skrift, chevron, Vis/Skjul, baggrund). */}
      {managerData && (
        <section className="pt-2">
          <button
            type="button"
            onClick={() => setKpiOpen((o) => !o)}
            className="w-full flex items-center justify-between gap-3 rounded-xl border border-border bg-secondary/50 hover:bg-secondary px-4 py-3.5 shadow-sm transition-colors group"
          >
            <span className="flex items-center gap-2.5">
              {kpiOpen ? (
                <ChevronUp className="w-5 h-5 text-[#FF2A6D]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#FF2A6D]" />
              )}
              <span className="text-lg font-bold text-foreground tracking-tight">
                KPI-detaljer
              </span>
              <Info
                className="w-4 h-4 text-muted-foreground/70 cursor-help"
                title="Sammensat score baseret på omsætning, elevtilfredshed, gns. kr/time og fremmøde i forhold til skolens mål."
              />
            </span>
            <span className="text-[13px] font-semibold text-[#FF2A6D]">
              {kpiOpen ? "Skjul" : "Vis"}
            </span>
          </button>

          {kpiOpen && (
            <div className="space-y-8 mt-6">
              {/* #48.3: by-vælger hører til her (admin + Administration/TNBC-medarbejdere).
                  Almindelige medarbejdere ser den ikke — KPI er låst til egen afdeling. */}
              {kanVaelgeBy && (
                <div className="flex gap-1 flex-wrap">
                  {adminCities.map((c) => (
                    <button
                      key={c}
                      onClick={() => setAdminKpiCity && setAdminKpiCity(c)}
                      className={`px-3 py-1.5 rounded-full text-[13px] font-semibold transition-colors duration-200 ${adminKpiCity === c ? "bg-background text-foreground border border-border shadow-sm" : "text-muted-foreground hover:text-foreground hover:bg-background/60 border border-transparent"}`}
                    >
                      {cityLabels[c] || c}
                    </button>
                  ))}
                </div>
              )}
              {/* KPI-OVERBLIK */}
              <div>
                <SectionTitle>KPI-overblik</SectionTitle>
                <KPICirclesRow data={managerData} />
              </div>
              {/* PERIODEHISTORIK 6 MDR */}
              <div>
                <SectionTitle>Periodehistorik 6 mdr.</SectionTitle>
                <MonthHistoryRow data={managerData} overallScore={overallScore} />
              </div>
              {/* BOOKINGGRAD */}
              <div>
                <SectionTitle>Bookinggrad</SectionTitle>
                <BookingBar
                  booking_pct={managerData.booking_pct}
                  booking_date={managerData.booking_date}
                  prev_booking_avg={managerData.prev_booking_avg}
                  booking_avg_period={managerData.booking_avg_period}
                  booking_goal_period={managerData.booking_goal_period}
                />
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default PerformanceView;
